import { Injectable } from '@angular/core';
import { addressString } from '@core/util/address';
import {
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { DateToShortDate } from '@core/util/datetime';
import { getPrimaryParcel } from '@core/util/parcels';
import { isSet } from '@core/util/value';
import { Business, BusinessAddress } from '@models/businesses';
import { ClientType } from '@models/clients';
import { IdentificationPriority } from '@models/identification';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	Matter,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { getIdentification } from '@state/global/tags/mappings/tags.identification';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class TD_FACE_TO_FACE_ID_Mapper extends BaseFormMapper<TD_FACE_TO_FACE_ID> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: TD_FACE_TO_FACE_ID,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): TD_FACE_TO_FACE_ID {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as TD_FACE_TO_FACE_ID);
		const filteredMatterClients = matterClients.filter(
			(x) => !isGuarantor(x) || mortgage.guarantors.includes(x.id),
		);

		if (!isSet(_formData?.fax)) {
			_formData.fax = '1-855-522-2755';
		}
		if (!isSet(_formData?.email)) {
			_formData.email = 'brokerfinalreports@td.com';
		}

		if (businessAddress?.phoneNumber && !isSet(_formData?.solicitorTelephone)) {
			_formData.solicitorTelephone = businessAddress.phoneNumber;
		}

		if (businessAddress?.faxNumber && !isSet(_formData?.solicitorFax)) {
			_formData.solicitorFax = businessAddress.faxNumber;
		}

		if (businessAddress.email && !isSet(_formData?.solicitorEmail)) {
			_formData.solicitorEmail = businessAddress.email;
		}

		if (!isSet(_formData?.date)) {
			_formData.date = format(new Date(), 'MMMM dd, yyyy');
		}

		if (mortgage?.referenceNumber && !isSet(_formData?.flexlineAccountNumber)) {
			_formData.flexlineAccountNumber = mortgage.referenceNumber;
		}

		if (matterClients?.length && !isSet(_formData?.mortgagors)) {
			_formData.mortgagors = joinNames(
				matterClients
					.filter((x) => !isGuarantor(x))
					.map((client) =>
						clientLegalName(client, nameFormattingSettings, {
							applyFormatting: false,
						}),
					),
			);
		}

		if (mortgage?.guarantors?.length && !isSet(_formData?.guarantors)) {
			_formData.guarantors = joinNames(
				matterClients
					?.filter((x) => isGuarantor(x) && mortgage.guarantors.includes(x.id))
					.map((client) =>
						clientLegalName(client, nameFormattingSettings, {
							applyFormatting: false,
						}),
					),
			);
		}

		if (
			matter?.propertyDetails?.address &&
			!isSet(_formData?.propertyAddress)
		) {
			_formData.propertyAddress = addressString(matter.propertyDetails.address);
		}

		if (matterTitles && !isSet(_formData?.propertyDescription)) {
			_formData.propertyDescription =
				getPrimaryParcel(matterTitles)?.description;
		}

		for (
			let index = 0;
			index < filteredMatterClients.length && index <= 3;
			index++
		) {
			const client =
				filteredMatterClients[index].type === ClientType.CORPORATION
					? getSignatories([filteredMatterClients[index]])[0]
					: (filteredMatterClients[
							index
						] as PersonMatterClientWithAssociations);
			const i = index + 1;
			const clientIdentification = getIdentification(
				client.identification?.[IdentificationPriority.PRIMARY],
				business?.defaultSettings?.shortDateFormat,
				business?.defaultSettings?.longDateFormat,
			);

			if (
				clientIdentification?.dateOfVerification &&
				!isSet(_formData[`individual${i}IdVerifiedDate`])
			) {
				_formData[`individual${i}IdVerifiedDate`] =
					clientIdentification.dateOfVerification;
			}

			if (
				clientIdentification?.nameOnId &&
				!isSet(_formData[`individual${i}FullName`])
			) {
				_formData[`individual${i}FullName`] = clientIdentification.nameOnId;
			}

			if (!isSet(_formData[`individual${i}DateOfBirth`])) {
				_formData[`individual${i}DateOfBirth`] = DateToShortDate(
					client.dateOfBirth,
					business?.defaultSettings?.shortDateFormat,
				);
			}

			if (
				clientIdentification?.displayText &&
				!isSet(_formData[`individual${i}IdType`])
			) {
				_formData[`individual${i}IdType`] = clientIdentification.displayText;
			}

			if (
				clientIdentification?.number &&
				!isSet(_formData[`individual${i}IdentificationNumber`])
			) {
				_formData[`individual${i}IdentificationNumber`] =
					clientIdentification.number;
			}

			if (
				clientIdentification?.jurisdiction &&
				!isSet(_formData[`individual${i}IssuingJurisdiction`])
			) {
				_formData[`individual${i}IssuingJurisdiction`] =
					clientIdentification.jurisdiction;
			}

			if (
				clientIdentification?.country &&
				!isSet(_formData[`individual${i}IssuingCountry`])
			) {
				_formData[`individual${i}IssuingCountry`] =
					clientIdentification.country;
			}

			if (
				clientIdentification?.expirationDate &&
				!isSet(_formData[`individual${i}ExpiryDate`])
			) {
				_formData[`individual${i}ExpiryDate`] =
					clientIdentification.expirationDate;
			}
		}

		if (matter?.lawyerAssigned?.name && !isSet(_formData?.solicitorName)) {
			_formData.solicitorName = matter?.lawyerAssigned?.name;
		}

		if (!isSet(_formData?.solicitorSignDate)) {
			_formData.solicitorSignDate = format(new Date(), 'MMMM dd, yyyy');
		}

		return _formData;
	}

	MapValues(): Partial<TD_FACE_TO_FACE_ID> {
		return {};
	}
}

const Mappings: TD_FACE_TO_FACE_ID = {
	fax: 'Fax',
	attention: 'Attention',
	from: 'From',
	email: 'Email',
	solicitorTelephone: 'Solicitor_Telephone',
	solicitorFax: 'Solicitor_Fax',
	solicitorEmail: 'Solicitor_Email',
	date: 'Date',
	flexlineAccountNumber: 'Flexline_Account_Number',
	mortgagors: 'Mortgagors',
	guarantors: 'Guarantors',
	propertyAddress: 'Property_Address',
	propertyDescription: 'Property_Description',
	individual2IdVerifiedDate: 'Individual_2_ID_Verified_Date',
	individual2FullName: 'Individual_2_Full_Name',
	individual2DateOfBirth: 'Individual_2_Date_of_Birth',
	individual2IdType: 'Individual_2_ID_Type',
	individual2IdentificationNumber: 'Individual_2_Identification_Number',
	individual2IssuingJurisdiction: 'Individual_2_Issuing_Jurisdiction',
	individual2IssuingCountry: 'Individual_2_Issuing_Country',
	individual2ExpiryDate: 'Individual_2_Expiry_Date',
	individual3IdVerifiedDate: 'Individual_3_ID_Verified_Date',
	individual3FullName: 'Individual_3_Full_Name',
	individual3DateOfBirth: 'Individual_3_Date_of_Birth',
	individual3IdType: 'Individual_3_ID_Type',
	individual3IdentificationNumber: 'Individual_3_Identification_Number',
	individual3IssuingJurisdiction: 'Individual_3_Issuing_Jurisdiction',
	individual3IssuingCountry: 'Individual_3_Issuing_Country',
	individual3ExpiryDate: 'Individual_3_Expiry_Date',
	individual1DateOfBirth: 'Individual_1_Date_of_Birth',
	individual4DateOfBirth: 'Individual_4_Date_of_Birth',
	individual1IdType: 'Individual_1_ID_Type',
	individual4IdType: 'Individual_4_ID_Type',
	individual1FullName: 'Individual_1_Full_Name',
	individual4FullName: 'Individual_4_Full_Name',
	individual1IdVerifiedDate: 'Individual_1_ID_Verified_Date',
	individual4IdVerifiedDate: 'Individual_4_ID_Verified_Date',
	individual1IdentificationNumber: 'Individual_1_Identification_Number',
	individual4IdentificationNumber: 'Individual_4_Identification_Number',
	individual1IssuingJurisdiction: 'Individual_1_Issuing_Jurisdiction',
	individual4IssuingJurisdiction: 'Individual_4_Issuing_Jurisdiction',
	individual1IssuingCountry: 'Individual_1_Issuing_Country',
	individual4IssuingCountry: 'Individual_4_Issuing_Country',
	individual1ExpiryDate: 'Individual_1_Expiry_Date',
	individual4ExpiryDate: 'Individual_4_Expiry_Date',
	solicitorName: 'Solicitor_Name',
	solicitorSignDate: 'Solicitor_Sign_Date',
};

export interface TD_FACE_TO_FACE_ID {
	fax: string;
	attention: string;
	from: string;
	email: string;
	solicitorTelephone: string;
	solicitorFax: string;
	solicitorEmail: string;
	date: string;
	flexlineAccountNumber: string;
	mortgagors: string;
	guarantors: string;
	propertyAddress: string;
	propertyDescription: string;
	individual2IdVerifiedDate: string;
	individual2FullName: string;
	individual2DateOfBirth: string;
	individual2IdType: string;
	individual2IdentificationNumber: string;
	individual2IssuingJurisdiction: string;
	individual2IssuingCountry: string;
	individual2ExpiryDate: string;
	individual3IdVerifiedDate: string;
	individual3FullName: string;
	individual3DateOfBirth: string;
	individual3IdType: string;
	individual3IdentificationNumber: string;
	individual3IssuingJurisdiction: string;
	individual3IssuingCountry: string;
	individual3ExpiryDate: string;
	individual1DateOfBirth: string;
	individual4DateOfBirth: string;
	individual1IdType: string;
	individual4IdType: string;
	individual1FullName: string;
	individual4FullName: string;
	individual1IdVerifiedDate: string;
	individual4IdVerifiedDate: string;
	individual1IdentificationNumber: string;
	individual4IdentificationNumber: string;
	individual1IssuingJurisdiction: string;
	individual4IssuingJurisdiction: string;
	individual1IssuingCountry: string;
	individual4IssuingCountry: string;
	individual1ExpiryDate: string;
	individual4ExpiryDate: string;
	solicitorName: string;
	solicitorSignDate: string;
}
